import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { Layout, LayoutGrid, Globe, ChevronDown, Search, Edit, Trash2, Upload, Plus, FileText, PenTool as Tool } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-hot-toast';

interface Page {
  id: string;
  slug: string;
  title: string;
  created_at: string;
}

interface ContentBlock {
  id: string;
  key: string;
  content: Record<string, any>;
  type: string;
}

interface ImportStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string;
  imported_at?: string;
  created_at: string;
}

export function AdminDashboard() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importStatuses, setImportStatuses] = useState<ImportStatus[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showFormatExample, setShowFormatExample] = useState(false);
  const [activeSection, setActiveSection] = useState<'pages' | 'tools' | 'blog'>('pages');
  const [tools, setTools] = useState<any[]>([]);
  const [blogPosts, setBlogPosts] = useState<any[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    fetchPages();
    fetchTools();
    fetchBlogPosts();
    fetchImportStatuses();
  }, [isAuthenticated, navigate]);

  const fetchTools = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_tools')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTools(data || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) throw error;
      setBlogPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
  };

  const fetchImportStatuses = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_tool_imports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImportStatuses(data || []);
    } catch (error) {
      console.error('Error fetching import statuses:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const data = JSON.parse(content);
          setPreviewData(data);
        } catch (error) {
          console.error('Error parsing file:', error);
          toast.error('Format de fichier invalide');
          setPreviewData(null);
        }
      };
      reader.readAsText(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setIsImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const content = e.target?.result as string;
          const tools = JSON.parse(content);

          const { data, error } = await supabase
            .from('ai_tool_imports')
            .insert({
              imported_by: (await supabase.auth.getUser()).data.user?.id,
              status: 'pending',
              source_data: tools
            })
            .select()
            .single();

          if (error) throw error;

          toast.success('Import initié avec succès');
          setShowImportModal(false);
          setImportFile(null);
          fetchImportStatuses();
        } catch (error) {
          console.error('Error importing tools:', error);
          toast.error('Erreur lors de l\'import');
        }
      };
      reader.readAsText(importFile);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Erreur lors de la lecture du fichier');
    } finally {
      setIsImporting(false);
    }
  };

  const fetchPages = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPages(data || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching pages:', error);
      setIsLoading(false);
    }
  };

  const fetchContentBlocks = async (pageId: string) => {
    try {
      const { data, error } = await supabase
        .from('content_blocks')
        .select('*')
        .eq('page_id', pageId)
        .order('key');

      if (error) throw error;
      setContentBlocks(data || []);
      setSelectedPage(pageId);
    } catch (error) {
      console.error('Error fetching content blocks:', error);
    }
  };

  const filteredBlocks = contentBlocks.filter(block => 
    block.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (typeof block.content[language] === 'string' && 
     block.content[language].toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Layout className="w-8 h-8 text-orange-500" />
              <h1 className="text-2xl font-bold text-gray-900">
                {activeSection === 'pages' ? 'Pages' :
                 activeSection === 'tools' ? 'Outils IA' :
                 'Articles Blog'}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Globe className="w-4 h-4" />
                <span className="uppercase">{language}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg shadow">
              <nav>
                <button
                  onClick={() => setActiveSection('pages')}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-left font-medium transition-colors
                    ${activeSection === 'pages'
                      ? 'bg-orange-50 text-orange-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <LayoutGrid className="w-5 h-5" />
                  Pages
                </button>
                <button
                  onClick={() => setActiveSection('tools')}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-left font-medium transition-colors
                    ${activeSection === 'tools'
                      ? 'bg-orange-50 text-orange-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Tool className="w-5 h-5" />
                  Outils IA
                </button>
                <button
                  onClick={() => setActiveSection('blog')}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-left font-medium transition-colors
                    ${activeSection === 'blog'
                      ? 'bg-orange-50 text-orange-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <FileText className="w-5 h-5" />
                  Blog
                </button>
              </nav>
              {activeSection === 'pages' && (
                <div className="p-2 border-t border-gray-200">
                  {isLoading ? (
                    <div className="p-4 text-center text-gray-500">Chargement...</div>
                  ) : (
                    pages.map(page => (
                      <button
                        key={page.id}
                        onClick={() => fetchContentBlocks(page.id)}
                        className={`w-full text-left px-4 py-2 rounded-md text-sm font-medium transition-colors
                          ${selectedPage === page.id
                            ? 'bg-orange-50 text-orange-700'
                            : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {page.title}
                        <span className="text-xs text-gray-500 ml-2">/{page.slug}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center justify-between">
                  <div className="relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder={`Rechercher ${
                        activeSection === 'pages' ? 'dans le contenu' :
                        activeSection === 'tools' ? 'un outil' :
                        'un article'
                      }...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                {activeSection !== 'pages' && (
                  <button
                    onClick={() => navigate(`/admin/${activeSection === 'tools' ? 'tools' : 'blog'}/new`)}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {activeSection === 'tools' ? 'Nouvel outil' : 'Nouvel article'}
                  </button>
                )}
              </div>
              <div className="p-4">
                {activeSection === 'pages' && !selectedPage ? (
                  <div className="text-center text-gray-500 py-8">
                    Sélectionnez une page pour voir son contenu
                  </div>
                ) : activeSection === 'pages' && filteredBlocks.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    Aucun contenu trouvé
                  </div>
                ) : activeSection === 'tools' ? (
                  <div className="space-y-4">
                    {tools.map(tool => (
                      <div
                        key={tool.id}
                        className="border rounded-lg p-4 hover:border-orange-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-medium">{tool.name}</h3>
                            <p className="text-sm text-gray-500">{tool.category}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/tools/edit/${tool.id}`)}
                              className="p-1 text-gray-400 hover:text-orange-500 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{tool.description}</p>
                      </div>
                    ))}
                  </div>
                ) : activeSection === 'blog' ? (
                  <div className="space-y-4">
                    {blogPosts.map(post => (
                      <div
                        key={post.id}
                        className="border rounded-lg p-4 hover:border-orange-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-medium">{post.title}</h3>
                            <p className="text-sm text-gray-500">
                              {new Date(post.published_at).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                              className="p-1 text-gray-400 hover:text-orange-500 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{post.excerpt}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredBlocks.map(block => (
                      <div
                        key={block.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-orange-200 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">{block.key}</h3>
                            <p className="text-xs text-gray-500">Type: {block.type}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="p-1 text-gray-400 hover:text-orange-500 transition-colors">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-gray-700">
                          {block.type === 'text' ? (
                            <p>{block.content[language]}</p>
                          ) : block.type === 'list' ? (
                            <ul className="list-disc list-inside">
                              {block.content[language].map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Import Tools Section */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-orange-500" />
                  Import d'outils IA
                </h2>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nouvel import
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                {importStatuses.map((status) => (
                  <div
                    key={status.id}
                    className="border rounded-lg p-4 flex items-center justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                          status.status === 'completed' ? 'bg-green-500' :
                          status.status === 'failed' ? 'bg-red-500' :
                          status.status === 'processing' ? 'bg-yellow-500' :
                          'bg-gray-500'
                        }`} />
                        <span className="font-medium">
                          Import {
                            status.status === 'completed' ? 'terminé' :
                            status.status === 'failed' ? 'échoué' :
                            status.status === 'processing' ? 'en cours' :
                            'en attente'
                          }
                        </span>
                      </div>
                      {status.error_message && (
                        <p className="text-sm text-red-500 mt-1">{status.error_message}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(status.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Importer des outils IA</h3>
            <p className="text-gray-600 mb-4">
              Sélectionnez un fichier JSON contenant les outils à importer.
            </p>
            
            <div className="flex items-center gap-4 mb-6">
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="flex-1"
              />
              <button
                onClick={() => setShowFormatExample(!showFormatExample)}
                className="text-orange-500 hover:text-orange-600 font-medium"
              >
                Voir le format
              </button>
            </div>

            {showFormatExample && (
              <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <h4 className="font-bold mb-2">Format attendu :</h4>
                <pre className="text-sm overflow-x-auto">
{`{
  "tools": [
    {
      "name": "Nom de l'outil",
      "description": "Description détaillée de l'outil",
      "category": "Catégorie (ex: Assistant IA, Génération d'images, etc.)",
      "pricing_type": "Type de tarification (ex: Gratuit, Freemium, Payant)",
      "pricing_details": "Détails des prix",
      "features": [
        "Fonctionnalité 1",
        "Fonctionnalité 2"
      ],
      "use_cases": [
        "Cas d'utilisation 1",
        "Cas d'utilisation 2"
      ],
      "pros": [
        "Avantage 1",
        "Avantage 2"
      ],
      "cons": [
        "Inconvénient 1",
        "Inconvénient 2"
      ],
      "website_url": "https://exemple.com",
      "image_url": "https://exemple.com/image.jpg"
    }
  ]
}`}
                </pre>
              </div>
            )}

            {previewData && (
              <div className="mb-6">
                <h4 className="font-bold mb-2">Prévisualisation :</h4>
                <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto">
                  <div className="space-y-4">
                    {previewData.tools?.map((tool: any, index: number) => (
                      <div key={index} className="border-b pb-4 last:border-0 last:pb-0">
                        <h5 className="font-medium">{tool.name}</h5>
                        <p className="text-sm text-gray-600">{tool.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                            {tool.category}
                          </span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {tool.pricing_type}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className={`bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 
                  ${!importFile || isImporting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'}`}
              >
                {isImporting ? 'Import en cours...' : 'Importer'}
                <Upload className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}